import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Button from '../../../core/components/Button.tsx';
import TagsField, { Tag, TagProps } from '../../../core/components/Fields/TagsField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import { CREATE_SMART_INBOX_INTEGRATION, GET_SMART_INBOX_INTEGRATIONS } from '../../../customer/queries.ts';
import { MAILBOX_DETAIL_PATH } from '../constants.ts';

interface AddMailboxModalProps {
    onClose: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const AddMailboxModal = ({ onClose }: AddMailboxModalProps) => {
    const { t } = useTranslation('mailboxes');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const apolloClient = useApolloClient();

    const [emails, setEmails] = useState<string[]>([]);
    const [isCreatingShared, setIsCreatingShared] = useState(false);

    const { data: mailboxesData } = useQuery(GET_SMART_INBOX_INTEGRATIONS);
    const connectedEmails = useMemo(
        () =>
            new Set<string>(
                (mailboxesData?.smartInboxIntegrations || []).map((mailbox: { mailInbox: string }) =>
                    normalizeEmail(mailbox.mailInbox)
                )
            ),
        [mailboxesData]
    );

    const uniqueEmails = [...new Set(emails.map((email) => email.trim()).filter(Boolean))];
    const invalidEmails = uniqueEmails.filter((email) => !EMAIL_PATTERN.test(email));
    const alreadyConnectedEmails = uniqueEmails.filter((email) => connectedEmails.has(normalizeEmail(email)));
    const isProblem = (email: string) => invalidEmails.includes(email) || alreadyConnectedEmails.includes(email);

    const errorMessage = invalidEmails.length
        ? t('addMailboxModal.sharedStep.invalidEmail')
        : alreadyConnectedEmails.length
          ? t('addMailboxModal.sharedStep.alreadyConnected', {
                count: alreadyConnectedEmails.length,
                emails: alreadyConnectedEmails.join(', '),
            })
          : undefined;
    const canConnect = uniqueEmails.length > 0 && !errorMessage;

    const [createSharedMailbox] = useMutation(CREATE_SMART_INBOX_INTEGRATION, {
        refetchQueries: [{ query: GET_SMART_INBOX_INTEGRATIONS }],
        awaitRefetchQueries: true,
    });

    const findMailboxId = (email: string): string | undefined =>
        apolloClient
            .readQuery({ query: GET_SMART_INBOX_INTEGRATIONS })
            ?.smartInboxIntegrations?.find((mailbox: { mailInbox: string }) => mailbox.mailInbox === email)?.id;

    const handleCreateShared = async () => {
        if (!canConnect) return;

        setIsCreatingShared(true);
        try {
            for (const email of uniqueEmails) {
                // the create mutation defaults to inactive (`enabled=False`), but a mailbox connected here should start
                // working right away
                await createSharedMailbox({ variables: { name: email, mailInbox: email, enabled: true } });
            }
            publishToast({
                description: t('addMailboxModal.sharedStep.createSuccess', { count: uniqueEmails.length }),
                status: 'success',
            });
            const createdMailboxId = uniqueEmails.length === 1 && findMailboxId(uniqueEmails[0]);
            if (createdMailboxId) {
                navigate(
                    url(MAILBOX_DETAIL_PATH, { id: createdMailboxId, tab: 'settings' }, { search: { type: 'shared' } })
                );
            }
            onClose();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('addMailboxModal.sharedStep.createError'), status: 'error' });
        } finally {
            setIsCreatingShared(false);
        }
    };

    const renderEmailTag = (props: TagProps) => (
        <Tag
            {...props}
            removeLabel={t('addMailboxModal.sharedStep.remove')}
            className={classnames(
                typeof props.children === 'string' &&
                    isProblem(props.children.trim()) &&
                    'border-line-danger-default bg-surface-danger-weak text-content-danger-strong'
            )}
        />
    );

    return (
        <Modal
            title={t('addMailboxModal.sharedStep.title')}
            onClose={onClose}
            className="sm:max-w-[40rem]"
            buttons={[
                <Button key="cancel" type="button" onClick={onClose} disabled={isCreatingShared}>
                    {t('addMailboxModal.cancel')}
                </Button>,
                <Button
                    key="connect"
                    type="button"
                    variant="primary"
                    loading={isCreatingShared}
                    disabled={!canConnect}
                    onClick={handleCreateShared}
                >
                    {t('addMailboxModal.connect')}
                </Button>,
            ]}
        >
            <div className="flex flex-col gap-4 text-sm">
                <p className="text-content-medium">{t('addMailboxModal.sharedStep.description')}</p>
                <FormField
                    label={t('addMailboxModal.sharedStep.fieldLabel')}
                    error={errorMessage && { message: errorMessage }}
                >
                    <TagsField
                        autoFocus
                        value={emails}
                        onValueChange={setEmails}
                        placeholder={t('addMailboxModal.sharedStep.placeholder')}
                        renderTag={renderEmailTag}
                        aria-label={t('addMailboxModal.sharedStep.fieldLabel')}
                    />
                </FormField>
            </div>
        </Modal>
    );
};

export default AddMailboxModal;
